import axiosInstance from "./axios-instance";
import { encryptionService } from "../core/encryption.service";

// Types for Mishna data structure
export interface FormattedMishna {
  id: number;
  name: string;
  text: string[];
}

export interface FormattedChapter {
  id: number;
  name: string;
  mishnayot: FormattedMishna[];
}

export interface FormattedMasechet {
  id: number;
  name: string;
  chapters: FormattedChapter[];
}

export interface BookContentResponse {
  version: string;
  data: FormattedMasechet[];
}

// Scrambled text always starts with this prefix ("Salted__" in base64).
const SCRAMBLED_PREFIX = 'U2FsdGVkX1';

// Unscramble one text only if it is scrambled. Running the unscrambler on plain
// Hebrew does not fail cleanly: about half the verses come back as an empty
// string. That is how a server answer sent unscrambled turned into missing verses.
const decryptIfScrambled = (text: string): string => {
  if (!text.startsWith(SCRAMBLED_PREFIX)) return text;
  try {
    const plain = encryptionService.decrypt(text);
    return plain === '' ? text : plain;
  } catch (error) {
    console.error('Failed to decrypt text:', error);
    return text;
  }
};

// Recursively decrypt all text fields in the data structure
export const decryptTextFields = (data: any): any => {
  if (Array.isArray(data)) {
    return data.map(decryptTextFields);
  }

  if (data && typeof data === 'object') {
    const decrypted = { ...data };

    // Decrypt the text field if it exists
    if (decrypted.text && typeof decrypted.text === 'string') {
      decrypted.text = decryptIfScrambled(decrypted.text);
    }

    // Also handle text arrays (like in Mishna)
    if (Array.isArray(decrypted.text)) {
      decrypted.text = decrypted.text.map((t: string) =>
        typeof t === 'string' ? decryptIfScrambled(t) : t
      );
    }

    // Recursively decrypt nested objects
    Object.keys(decrypted).forEach(key => {
      if (key !== 'text' && typeof decrypted[key] === 'object') {
        decrypted[key] = decryptTextFields(decrypted[key]);
      }
    });

    return decrypted;
  }

  return data;
};

/**
 * Fetches only the version string of every book. About 200 bytes, no text.
 *
 * This exists so the client can learn that content changed WITHOUT downloading
 * the content. Reading the version out of the cached book payload cannot work:
 * that value and the one in localStorage both come from the same fetch, so they
 * can never disagree.
 *
 * Returns e.g. { torah: "1.0.5", mishna: "1.0.1", neviim: "1.0.2" }
 */
export const fetchBookVersions = async (): Promise<Record<string, string>> => {
  const response = await axiosInstance.get('/book-versions');
  return response.data as Record<string, string>;
};

export const fetchBookContent = async (bookName: string): Promise<BookContentResponse> => {
  try {
    // בחירת נקודת הקצה הנכונה לפי סוג הספר
    let endpoint: string;
    if (bookName.toLowerCase() === 'siddur_ashkenaz' || bookName.toLowerCase() === 'siddur-ashkenaz') {
      endpoint = '/get-siddur-ashkenaz-data';
    } else {
      endpoint = `/get-${bookName}-data`;
    }
    
    const response = await axiosInstance.get(endpoint);
    let result = response.data as BookContentResponse;

    // Handle backward compatibility: if response doesn't have version, it's old format
    if (!result.version && Array.isArray(result)) {
      result = {
        version: "0.0.0", // Unknown version for old format
        data: result as FormattedMasechet[]
      };
    }

    // Decrypt all text fields in the response
    result.data = decryptTextFields(result.data);

    return result;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error(
      `Error fetching content for book ${bookName}:`,
      errorMessage
    );
    throw error;
  }
};

 